import {
  AdminAccountsOverview,
  AdminClient,
  AdminExecution,
  AdminExecutionDetail,
  AdminFlow,
  AdminHealthOverview,
  AdminMcp,
  AdminUser,
  FairQueueStats,
  ListAdminClientsRequest,
  ListAdminExecutionsRequest,
  ListAdminFlowsRequest,
  ListAdminMcpsRequest,
  ListAdminUsersRequest,
  ListSwaggerDefinitionsRequest,
  McpRun,
  McpRunStatus,
  McpWithTools,
  PopulatedFlow,
  SeekPage,
  KizeoAdminSession,
  KizeoTenantSyncState,
  TenantPolicy,
  TenantSyncPreview,
  SwaggerDefinition,
  SwaggerDefinitionSummary,
  TenantSyncResult,
  UpdateSwaggerDefinitionRequest,
  Usage,
  CreateSwaggerDefinitionRequest,
} from '@activepieces/shared';

import { api } from '@/lib/api';

export const adminApi = {
  getDashboardHealth(): Promise<AdminHealthOverview> {
    return api.get<AdminHealthOverview>('/v1/internal-admin/dashboard/health');
  },
  getDashboardAccounts(): Promise<AdminAccountsOverview> {
    return api.get<AdminAccountsOverview>(
      '/v1/internal-admin/dashboard/accounts',
    );
  },
  getFairQueueStats(tenantIds?: string[]): Promise<FairQueueStats> {
    return api.get<FairQueueStats>(
      '/v1/internal-admin/fair-queue/stats',
      tenantIds && tenantIds.length > 0
        ? { tenantIds: tenantIds.join(',') }
        : undefined,
    );
  },
  getSession(): Promise<KizeoAdminSession> {
    return api.get<KizeoAdminSession>('/v1/internal-admin/session');
  },
  listClients(
    request: ListAdminClientsRequest,
  ): Promise<SeekPage<AdminClient>> {
    return api.get<SeekPage<AdminClient>>(
      '/v1/internal-admin/clients',
      request,
    );
  },
  listFlows(request: ListAdminFlowsRequest): Promise<SeekPage<AdminFlow>> {
    return api.get<SeekPage<AdminFlow>>('/v1/internal-admin/flows', request);
  },
  listExecutions(
    request: ListAdminExecutionsRequest,
  ): Promise<SeekPage<AdminExecution>> {
    return api.get<SeekPage<AdminExecution>>(
      '/v1/internal-admin/executions',
      request,
    );
  },
  listUsers(request: ListAdminUsersRequest): Promise<SeekPage<AdminUser>> {
    return api.get<SeekPage<AdminUser>>('/v1/internal-admin/users', request);
  },
  listMcps(request: ListAdminMcpsRequest): Promise<SeekPage<AdminMcp>> {
    return api.get<SeekPage<AdminMcp>>('/v1/internal-admin/mcps', request);
  },
  getClient(clientId: string): Promise<AdminClient> {
    return api.get<AdminClient>(`/v1/internal-admin/clients/${clientId}`);
  },
  getClientUsage(clientId: string): Promise<Usage> {
    return api.get<Usage>(
      `/v1/internal-admin/clients/${clientId}/usage`,
    );
  },
  getClientPolicy(clientId: string): Promise<TenantPolicy> {
    return api.get<TenantPolicy>(
      `/v1/internal-admin/clients/${clientId}/policy`,
    );
  },
  getSyncPreview(clientId: string): Promise<TenantSyncPreview> {
    return api.get<TenantSyncPreview>(
      `/v1/internal-admin/clients/${clientId}/sync-preview`,
    );
  },
  runSync(clientId: string): Promise<TenantSyncResult> {
    return api.post<TenantSyncResult>(
      `/v1/internal-admin/clients/${clientId}/sync`,
    );
  },
  getSyncState(clientId: string): Promise<KizeoTenantSyncState | null> {
    return api.get<KizeoTenantSyncState | null>(
      `/v1/internal-admin/clients/${clientId}/sync-state`,
    );
  },
  getUser(userId: string): Promise<AdminUser> {
    return api.get<AdminUser>(`/v1/internal-admin/users/${userId}`);
  },
  getExecution(tenantId: string, runId: string): Promise<AdminExecutionDetail> {
    return api.get<AdminExecutionDetail>(
      `/v1/internal-admin/executions/${tenantId}/${runId}`,
    );
  },
  getFlow(tenantId: string, flowId: string): Promise<PopulatedFlow> {
    return api.get<PopulatedFlow>(`/v1/internal-admin/flows/${tenantId}/${flowId}`);
  },
  getMcp(tenantId: string, mcpId: string): Promise<McpWithTools> {
    return api.get<McpWithTools>(`/v1/internal-admin/mcps/${tenantId}/${mcpId}`);
  },
  listSwaggers(
    request: ListSwaggerDefinitionsRequest,
  ): Promise<SeekPage<SwaggerDefinitionSummary>> {
    return api.get<SeekPage<SwaggerDefinitionSummary>>(
      '/v1/internal-admin/swaggers',
      request,
    );
  },
  getSwagger(id: string): Promise<SwaggerDefinition> {
    return api.get<SwaggerDefinition>(`/v1/internal-admin/swaggers/${id}`);
  },
  createSwagger(
    request: CreateSwaggerDefinitionRequest,
  ): Promise<SwaggerDefinition> {
    return api.post<SwaggerDefinition>(
      '/v1/internal-admin/swaggers',
      request,
    );
  },
  updateSwagger(
    id: string,
    request: UpdateSwaggerDefinitionRequest,
  ): Promise<SwaggerDefinition> {
    return api.patch<SwaggerDefinition>(
      `/v1/internal-admin/swaggers/${id}`,
      request,
    );
  },
  refreshSwagger(id: string): Promise<SwaggerDefinition> {
    return api.post<SwaggerDefinition>(
      `/v1/internal-admin/swaggers/${id}/refresh`,
    );
  },
  deleteSwagger(id: string): Promise<void> {
    return api.delete<void>(`/v1/internal-admin/swaggers/${id}`);
  },
  listMcpRuns(
    tenantId: string,
    mcpId: string,
    request: {
      cursor?: string;
      limit?: number;
      status?: McpRunStatus[];
      metadata?: string;
    },
  ): Promise<SeekPage<McpRun>> {
    return api.get<SeekPage<McpRun>>(
      `/v1/internal-admin/mcps/${tenantId}/${mcpId}/runs`,
      request,
    );
  },
};
